// Shared Horse Racing name presentation. The race world, tote, results and
// standalone leaderboards all use the same fallback and grapheme-safe wrapping
// rules so a renderer never needs to shorten a runner's name.

export const HORSE_NAME_FALLBACK = 'Unnamed runner';

export function displayHorseName(value) {
  if (value == null) return HORSE_NAME_FALLBACK;
  const text = String(value);
  return text.trim() ? text : HORSE_NAME_FALLBACK;
}

function fallbackGraphemes(text) {
  const result = [];
  const regional = /[\u{1f1e6}-\u{1f1ff}]/u;
  const extension = /[\p{Mark}\ufe0e\ufe0f\u{1f3fb}-\u{1f3ff}]/u;
  for (const codePoint of Array.from(text)) {
    const previous = result[result.length - 1] || '';
    const regionalPair = regional.test(codePoint)
      && regional.test(previous)
      && Array.from(previous).filter((part) => regional.test(part)).length % 2 === 1;
    if (
      result.length
      && (extension.test(codePoint) || codePoint === '\u200d'
        || previous.endsWith('\u200d') || regionalPair)
    ) {
      result[result.length - 1] += codePoint;
    } else {
      result.push(codePoint);
    }
  }
  return result;
}

export function horseNameGraphemes(value) {
  const text = displayHorseName(value);
  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    return Array.from(segmenter.segment(text), ({ segment }) => segment);
  }
  return fallbackGraphemes(text);
}

/**
 * Wrap a complete horse name without adding or removing any characters.
 *
 * `measure` returns a width for a string in the caller's rendering font.
 * The declared width grows only when one indivisible grapheme itself is wider
 * than the requested limit; otherwise long tokens continue on extra lines.
 */
export function layoutHorseName(value, { measure, maxWidth }) {
  if (typeof measure !== 'function') throw new TypeError('measure must be a function');
  if (!(maxWidth > 0)) throw new RangeError('maxWidth must be positive');

  const text = displayHorseName(value);
  const graphemes = horseNameGraphemes(text);
  const width = (part) => Number(measure(part)) || 0;
  const declaredWidth = graphemes.reduce(
    (widest, grapheme) => Math.max(widest, width(grapheme)),
    maxWidth,
  );
  const lines = [];
  const lineWidths = [];

  let start = 0;
  while (start < graphemes.length) {
    let end = start;
    let lastWhitespaceBreak = -1;

    while (end < graphemes.length) {
      const candidate = graphemes.slice(start, end + 1).join('');
      if (width(candidate) > declaredWidth) break;
      if (/\s/u.test(graphemes[end])) lastWhitespaceBreak = end + 1;
      end += 1;
    }

    let stop;
    if (end === graphemes.length) stop = end;
    else if (lastWhitespaceBreak > start) stop = lastWhitespaceBreak;
    else stop = Math.max(start + 1, end);

    const line = graphemes.slice(start, stop).join('');
    lines.push(line);
    lineWidths.push(width(line));
    start = stop;
  }

  return { text, lines, lineWidths, maxWidth: declaredWidth };
}
